import { Box, Flex, UnstyledButton } from "@mantine/core";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useState, type ReactNode } from "react";

type CopyableTextProps = {
  children: ReactNode;
  textToCopy: string;
  gap?: string;
  align?: "flex-start" | "center" | "flex-end";
  /** Position of the copy icon. "end" keeps the icon at the end (right) when text wraps. */
  iconPosition?: "start" | "end";
};

const CopyableText = ({
  children,
  textToCopy,
  gap = "var(--spacing-x-small)",
  align = "flex-start",
  iconPosition = "start",
}: CopyableTextProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyToClipboard = async () => {
    if (!textToCopy || textToCopy === "-") return;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const flexStyle =
    iconPosition === "end"
      ? { justifyContent: "space-between" as const, width: "100%" }
      : undefined;
  const content =
    iconPosition === "end" ? (
      <>
        <Box style={{ flex: 1, minWidth: 0 }}>{children}</Box>
        <UnstyledButton onClick={handleCopyToClipboard} style={{ flexShrink: 0 }}>
          {isCopied ? (
            <IconCheck size={14} color="var(--icon-success-default)" />
          ) : (
            <IconCopy size={14} color="var(--icon-default)" />
          )}
        </UnstyledButton>
      </>
    ) : (
      <>
        {children}
        <UnstyledButton onClick={handleCopyToClipboard}>
          {isCopied ? (
            <IconCheck size={14} color="var(--icon-success-default)" />
          ) : (
            <IconCopy size={14} color="var(--icon-default)" />
          )}
        </UnstyledButton>
      </>
    );

  return (
    <Flex align={align} gap={gap} style={flexStyle}>
      {content}
    </Flex>
  );
};

export default CopyableText;
